import { alpha, Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { type StatusChecklistItem, SystemStatusChecklist } from "../../../../../components/UIToolkit/molecules/SystemStatusChecklist";
import { ALPHA, FONT_SIZE } from "../../../../../theme/tokens";

type Props = {
	/** The Clipboard login is stored for this department — it is enumerated by the sync at all. */
	enrolled: boolean;
	/** `clipboard_facility_id` is set — which building, AND outbound posting to the agency is open. */
	facilityMapped: boolean;
	/** An active `(clipboard, shifts)` HRIS row — actual punches only. */
	actualsOn: boolean;
	/** The wizard is currently working on this switch. */
	activeSwitch?: "enrollment" | "facility";
};

/**
 * The three independent Clipboard switches, kept on screen for the whole wizard.
 *
 * They are genuinely separate — a department can be enrolled with no facility mapped, or mapped with
 * actuals deliberately off — and every silent half-configured department in production is a case of one
 * of them being missed. Showing all three, with the one being worked on called out, is what stops the
 * wizard from reading as "a form with steps" and makes it obvious that finishing it still leaves actual
 * punches off until someone flips the toggle on the Integrations tab.
 */
export const ClipboardSwitchesAside: FC<Props> = ({ enrolled, facilityMapped, actualsOn, activeSwitch }) => {
	const { t } = useTranslation();

	const stateFor = (done: boolean, key: Props["activeSwitch"]): StatusChecklistItem["state"] => (done ? "done" : activeSwitch === key ? "active" : "pending");

	const items: StatusChecklistItem[] = [
		{
			id: "enrollment",
			label: t("department.config.clipboard.wizard.switches.enrollment.label"),
			description: t("department.config.clipboard.wizard.switches.enrollment.description"),
			state: stateFor(enrolled, "enrollment"),
			statusLabel: enrolled ? t("department.config.clipboard.wizard.switches.state.on") : t("department.config.clipboard.wizard.switches.state.off"),
			dataTestId: "clipboard-wizard-switch-enrollment",
		},
		{
			id: "facility",
			label: t("department.config.clipboard.wizard.switches.facility.label"),
			description: t("department.config.clipboard.wizard.switches.facility.description"),
			state: stateFor(facilityMapped, "facility"),
			statusLabel: facilityMapped ? t("department.config.clipboard.wizard.switches.state.on") : t("department.config.clipboard.wizard.switches.state.off"),
			dataTestId: "clipboard-wizard-switch-facility",
		},
		{
			id: "actuals",
			label: t("department.config.clipboard.wizard.switches.actuals.label"),
			description: t("department.config.clipboard.wizard.switches.actuals.description"),
			// Never "active": the actuals row rides the config form's own dirty → Save flow, so the wizard
			// can only report it, never set it.
			state: actualsOn ? "done" : "pending",
			statusLabel: actualsOn ? t("department.config.clipboard.wizard.switches.state.on") : t("department.config.clipboard.wizard.switches.state.off"),
			dataTestId: "clipboard-wizard-switch-actuals",
		},
	];

	return (
		<Box
			data-testid="clipboard-wizard-switches"
			sx={(theme) => ({
				flexShrink: 0,
				width: { xs: "100%", md: 300 },
				p: 2,
				borderRadius: 2,
				backgroundColor: alpha(theme.palette.primary.main, ALPHA.HERO_WASH),
			})}
		>
			<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: "text.secondary", mb: 1.5, lineHeight: 1.5 }}>
				{t("department.config.clipboard.wizard.switches.intro")}
			</Typography>
			<SystemStatusChecklist items={items} ariaLabel={t("department.config.clipboard.wizard.switches.ariaLabel")} dataTestId="clipboard-wizard-switches-list" />
		</Box>
	);
};
